import React, { useEffect, useMemo, useReducer, useRef, useState } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Send, MicOff, ShoppingBag, ArrowLeft, Sparkles, Loader2, Phone, Mail, MapPin, MessageCircle, X, Menu as MenuIcon } from "lucide-react";
import { cleanImageUrl, resolveTenantLogoUrl } from "../lib/retailFormat";
import useRetailCatalog from "../hooks/retail/useRetailCatalog";
import { customerKey, storefrontPath } from "../lib/retailSessionKeys";

// Phase 3L.1 Pass B — Full-page Rudras-AI Assistant for the storefront.
//
// Iter62 (Feb 2026) — Restored guided chat experience:
//  • Field-name bug fixed: we now read `answer` (the backend's friendly
//    alias) so the assistant's reply actually surfaces instead of the
//    "Sorry, I didn't catch that" fallback.
//  • Persistent left-rail FAQ + Contact panel. Quick prompts and contact
//    info STAY visible after the user clicks one — they no longer
//    disappear with the hero. On mobile this collapses to a drawer.
//  • Product cards from `data.cards` are now rendered inline under the
//    assistant's turn.
//  • Idle auto-close after 5 minutes returns the user to the storefront
//    so the cart context is preserved.
//  • Tenant identity remains dominant (logo / name / "AI Assistant · Live").

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const RETAIL_BASE = `${BACKEND_URL}/api/retail/public`;
const SESSION_KEY = "retail_session_id";
const IDLE_TIMEOUT_MS = 5 * 60 * 1000; // 5 min

function ensureSessionId() {
  let sid = sessionStorage.getItem(SESSION_KEY);
  if (!sid) {
    sid = `r-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`;
    sessionStorage.setItem(SESSION_KEY, sid);
  }
  return sid;
}

function transcriptReducer(state, action) {
  switch (action.type) {
    case "seed-greeting": {
      if (state.length > 0) return state;
      // Slice 1 (Feb 2026) — persona greeting wins when the backend
      // surfaces one. Falls back to the legacy `assistant_greeting`
      // field, then to a generic line. The persona greeting already
      // names the assistant + tenant.
      const greeting =
        action.personaGreeting
        || action.tenant?.assistant_meta?.greeting
        || action.tenant?.assistant_greeting
        || `Hi, I'm the AI assistant for ${action.tenant?.name || "this shop"}. Ask me anything about the menu, or tap "Start Sales Order" when you're ready.`;
      return [{ role: "assistant", text: greeting, ts: Date.now() }];
    }
    case "user":      return [...state, { role: "user",      text: action.text, ts: Date.now() }];
    case "assistant": return [...state, { role: "assistant", text: action.text, cards: action.cards || [], pendingAction: action.pendingAction || null, ts: Date.now() }];
    default:          return state;
  }
}

export default function RetailChatPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  // Parcel Shipping Checkout Enforcement — Phase 7. A direct visit to
  // /chat with no `?return=` (e.g. a bookmarked/shared link) used to
  // fall back to the bare tenant storefront, which can silently swap a
  // catalog-specific storefront for the tenant's default catalog.
  const returnTo = params.get("return") || storefrontPath(slug);

  const { loading, gateError, catalogData } = useRetailCatalog(slug);
  const tenant = catalogData?.tenant || null;
  const catalog = catalogData?.catalog || null;
  const categories = useMemo(() => catalogData?.categories || [], [catalogData]);

  // Slice 1 (Feb 2026) — Sales AI Persona surfaced by the backend.
  // Falls back gracefully when the backend hasn't been redeployed yet.
  const assistantName = tenant?.assistant_meta?.display_name || "";
  const assistantRole = tenant?.assistant_meta?.role_caption || "";
  const personaGreeting = tenant?.assistant_meta?.greeting || "";

  const [sessionId] = useState(ensureSessionId);
  const [transcript, dispatch] = useReducer(transcriptReducer, []);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false); // mobile drawer toggle
  const scrollRef = useRef(null);
  const idleTimerRef = useRef(null);

  // Restaurant-Pilot Fix Sprint (Feb 2026, P1-2) — Chat ↔ Sales Order
  // context sync. When the customer started a Sales Order earlier (and
  // therefore typed their name into the welcome modal), RetailCustomer
  // persists a tenant-scoped `customerKey(slug)` record in
  // sessionStorage. Reading it here so the chat greeting + backend
  // prompt include the first name. Cleared automatically by
  // Start-New-Order, so context resets correctly.
  //
  // PR #41 hardening — this used to read the UNSCOPED legacy key
  // `"retail_customer_v1"`, a stale reference from before the cross-
  // tenant session-leak fix scoped it to `retail_customer_v1:{slug}` —
  // nothing has written to the unscoped key since, so this greeting
  // had been silently non-functional for every session.
  const knownCustomer = (() => {
    try {
      const raw = sessionStorage.getItem(customerKey(slug));
      return raw ? JSON.parse(raw) : null;
    } catch (_e) { return null; }
  })();
  const customerFirstName = (knownCustomer?.name || "").trim().split(/\s+/)[0] || "";

  useEffect(() => {
    if (!catalogData) return;
    // Restaurant-Pilot Fix Sprint (Feb 2026, P1-2) — when the customer
    // already gave us their name via Sales Order, prepend a soft
    // acknowledgement to the persona greeting so chat doesn't restart
    // the relationship from scratch.
    const baseGreeting = personaGreeting
      || catalogData?.tenant?.assistant_meta?.greeting
      || "";
    const greetingWithName = (customerFirstName && baseGreeting)
      ? `Hi ${customerFirstName} — ${baseGreeting.replace(/^Hi[,!\s]*[A-Za-z']*[,!\s]*/i, "")}`
      : baseGreeting;
    dispatch({
      type: "seed-greeting",
      tenant: catalogData.tenant,
      personaGreeting: greetingWithName,
    });
  }, [catalogData, personaGreeting, customerFirstName]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [transcript, sending]);

  // Iter62 — 5-min idle auto-close. Any user activity resets the timer.
  // On expiry we navigate back to the storefront so the cart context is
  // preserved (sessionStorage already holds cart + session_id).
  useEffect(() => {
    const reset = () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => {
        navigate(returnTo, { replace: false });
      }, IDLE_TIMEOUT_MS);
    };
    reset();
    const events = ["mousemove", "keydown", "click", "touchstart"];
    events.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    return () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      events.forEach((e) => window.removeEventListener(e, reset));
    };
  }, [navigate, returnTo]);

  const send = async (raw) => {
    const text = (raw ?? input).trim();
    if (!text || sending) return;
    setInput("");
    dispatch({ type: "user", text });
    setSending(true);
    try {
      const { data } = await axios.post(`${RETAIL_BASE}/${slug}/chat`, {
        message: text,
        session_id: sessionId,
        // Restaurant-Pilot Fix Sprint (Feb 2026, P1-2) — forward
        // the customer's first name so Maya can address them by
        // name in the LLM-composed responses.
        customer_name: customerFirstName || undefined,
      });
      // Iter62 — backend exposes BOTH `answer` and `answer_text`; we read
      // either so the chat works against new + legacy backends. Product
      // cards come from `data.cards`.
      const reply = data?.answer || data?.answer_text || data?.message || data?.reply
        || "I'm here. Could you ask me about the menu or contact us?";
      const cards = Array.isArray(data?.cards) ? data.cards : [];
      // Slice 4 (Feb 2026) — Assistant Add-to-Cart offer. Server-built
      // pending_action dict. Only honored when `kind === "add_to_cart_offer"`
      // AND `requires_explicit_confirmation` is true. We never auto-add.
      const pending = data?.pending_action;
      const pendingAction = (pending && pending.kind === "add_to_cart_offer" && pending.requires_explicit_confirmation)
        ? pending
        : null;
      dispatch({ type: "assistant", text: reply, cards, pendingAction });
    } catch (_e) {
      dispatch({
        type: "assistant",
        text: "I'm having trouble reaching the assistant right now. You can still tap Start Sales Order below to browse the menu.",
        cards: [],
      });
    } finally {
      setSending(false);
    }
  };

  // Quick prompts — tenant-specific when categories are available, else generic.
  const quickPrompts = useMemo(() => {
    const cats = (categories || []).map((c) => c.name).filter(Boolean).slice(0, 4);
    if (cats.length) {
      return [
        `Show me ${cats[0]}`,
        ...cats.slice(1).map((c) => `Show me ${c}`),
        "What's good for two people?",
        "What are your hours?",
      ];
    }
    return [
      "What's most popular?",
      "Anything vegetarian?",
      "What are your hours?",
      "How can I contact you?",
    ];
  }, [categories]);

  // Iter41 — operator-disabled Chat Assistant.
  useEffect(() => {
    if (catalog && catalog.chat_assistant_enabled === false) {
      navigate(returnTo, { replace: true });
    }
  }, [catalog, navigate, returnTo]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0d1f12] grid place-items-center text-emerald-100" data-testid="retail-chat-loading">
        <Loader2 className="w-7 h-7 animate-spin text-orange-400" />
      </div>
    );
  }
  if (gateError) {
    return (
      <div className="min-h-screen bg-[#0d1f12] grid place-items-center px-6 text-center text-stone-100">
        <div className="max-w-sm">
          <h1 className="text-2xl font-semibold mb-2">Assistant unavailable</h1>
          <p className="text-stone-300 mb-6" data-testid="retail-chat-gate-error">{gateError}</p>
          <Link to="/" className="text-sm text-stone-300 underline">Return home</Link>
        </div>
      </div>
    );
  }
  if (catalog && catalog.chat_assistant_enabled === false) return null;

  const tenantName = tenant?.name || catalog?.name || "Our Kitchen";
  const contactPhone = tenant?.contact_phone || tenant?.phone || "";
  const contactEmail = tenant?.contact_email || tenant?.email || "";
  const address = tenant?.address || "";
  // Restaurant Sales Assistant Unification (Mar 2026) — branding +
  // service-modes data sourced from the public catalog payload.
  const tagline = tenant?.tagline || tenant?.description || "";
  const businessHours = tenant?.business_hours || "";
  const orderModes = catalog?.order_modes || {};
  const orderModeLabels = catalog?.order_mode_labels || {};
  const enabledModes = Object.keys(orderModes).filter(
    (k) => orderModes[k] && orderModes[k].enabled
  );
  const MODE_VISUAL = {
    dine_in:  { icon: "🍽", label: "Dine-In",  desc: "Warm sit-down experience" },
    takeout:  { icon: "🛍", label: "To-Go",    desc: "Order ahead, pick up fresh" },
    pickup:   { icon: "🛍", label: "Pickup",   desc: "Order ahead, pick up fresh" },
    delivery: { icon: "🚚", label: "Delivery", desc: "We bring it to you" },
    catering: { icon: "🎉", label: "Catering", desc: "Parties, events & corporate" },
  };
  const goToOrderWithMode = (mode) => {
    // Pass the mode via URL so RetailCustomer can preselect it in the
    // welcome modal (RetailCustomer reads ?orderType=). Falls back to
    // simple navigation if mode is empty.
    const path = mode
      ? `${returnTo}${returnTo.includes("?") ? "&" : "?"}orderType=${encodeURIComponent(mode)}`
      : returnTo;
    navigate(path);
  };

  // Persistent side panel content (rendered on desktop + mobile drawer).
  const SidebarContent = (
    <div className="flex flex-col gap-5 p-5 text-sm" data-testid="retail-chat-sidebar-content">
      {/* Branding card — restaurant-front-desk identity */}
      <div className="rounded-xl border border-emerald-200/20 bg-emerald-900/30 p-4" data-testid="retail-chat-branding-card">
        <div className="flex items-center gap-3">
          {tenant?.logo_url ? (
            <img src={resolveTenantLogoUrl(tenant.logo_url)} alt={`${tenantName} logo`} className="w-10 h-10 rounded-lg object-contain bg-black" />
          ) : (
            <div className="w-10 h-10 rounded-lg bg-orange-500/20 grid place-items-center text-orange-300 font-semibold">
              {(tenantName || "?").slice(0, 1)}
            </div>
          )}
          <div className="min-w-0">
            <div className="font-serif text-emerald-50 text-base leading-tight truncate" data-testid="retail-chat-branding-name">{tenantName}</div>
            {tagline ? (
              <div className="text-[11px] text-emerald-200/70 truncate" data-testid="retail-chat-branding-tagline">{tagline}</div>
            ) : null}
          </div>
        </div>
        {businessHours ? (
          <div className="mt-3 text-[11px] text-emerald-200/70 flex items-start gap-1.5" data-testid="retail-chat-branding-hours">
            <span>🕐</span><span>{businessHours}</span>
          </div>
        ) : null}
      </div>

      {/* Our Services — service-mode chips */}
      {enabledModes.length > 0 && (
        <div data-testid="retail-chat-service-modes">
          <div className="text-[10px] uppercase tracking-widest text-emerald-200/60 mb-2">
            Our services
          </div>
          <div className="flex flex-col gap-2">
            {enabledModes.map((m) => {
              const v = MODE_VISUAL[m] || { icon: "•", label: orderModeLabels[m] || m, desc: "" };
              return (
                <button
                  key={m}
                  type="button"
                  onClick={() => { goToOrderWithMode(m); setSidebarOpen(false); }}
                  data-testid={`retail-chat-service-${m}`}
                  className="w-full text-left px-3 py-2.5 rounded-lg border border-emerald-200/15 bg-black/30 hover:bg-black/50 hover:border-orange-400/40 flex items-center gap-3 transition"
                >
                  <span className="text-xl leading-none">{v.icon}</span>
                  <span className="min-w-0">
                    <span className="block text-sm text-emerald-50 font-semibold">{orderModeLabels[m] || v.label}</span>
                    {v.desc ? <span className="block text-[10px] text-emerald-200/60">{v.desc}</span> : null}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Quick questions — big-tappable rows (restaurant-front-desk style) */}
      <div>
        <div className="text-[10px] uppercase tracking-widest text-emerald-200/60 mb-2">
          Quick questions
        </div>
        <div className="flex flex-col gap-1.5" data-testid="retail-chat-quick-prompts">
          {quickPrompts.map((q) => (
            <button
              key={q}
              type="button"
              onClick={() => { send(q); setSidebarOpen(false); }}
              data-testid={`retail-chat-quick-${q.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 30)}`}
              className="text-left text-sm px-3 py-2.5 rounded-lg bg-black/30 border border-emerald-200/15 text-emerald-100 hover:bg-black/50 hover:border-orange-400/40 flex items-center justify-between gap-2 transition"
            >
              <span className="truncate">{q}</span>
              <span className="text-emerald-200/40">›</span>
            </button>
          ))}
        </div>
      </div>

      <div className="h-px bg-emerald-900/40" />

      <div data-testid="retail-chat-contact">
        <div className="text-[10px] uppercase tracking-widest text-emerald-200/60 mb-2">
          Contact us
        </div>
        <div className="flex flex-col gap-2 text-emerald-100/90">
          {contactPhone ? (
            <a href={`tel:${contactPhone}`} className="flex items-center gap-2 hover:text-orange-300" data-testid="retail-chat-contact-phone">
              <Phone className="w-4 h-4 text-emerald-300" />
              <span>{contactPhone}</span>
            </a>
          ) : null}
          {contactEmail ? (
            <a href={`mailto:${contactEmail}`} className="flex items-center gap-2 hover:text-orange-300" data-testid="retail-chat-contact-email">
              <Mail className="w-4 h-4 text-emerald-300" />
              <span className="break-all">{contactEmail}</span>
            </a>
          ) : null}
          {address ? (
            <div className="flex items-start gap-2" data-testid="retail-chat-contact-address">
              <MapPin className="w-4 h-4 text-emerald-300 mt-0.5 shrink-0" />
              <span className="text-emerald-100/85">{address}</span>
            </div>
          ) : null}
        </div>
      </div>

      <div className="h-px bg-emerald-900/40" />

      <button
        type="button"
        onClick={() => navigate(returnTo)}
        data-testid="retail-chat-sidebar-start-order"
        className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-full bg-orange-500 text-white text-sm font-semibold hover:bg-orange-600"
      >
        <ShoppingBag className="w-4 h-4"/> Start Sales Order
      </button>
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col bg-[#0d1f12] text-stone-100" data-testid="retail-chat-page">
      {/* Header */}
      <header className="bg-[#0a1a0e] border-b-4 border-orange-500 px-4 sm:px-6 py-3 flex items-center gap-3 shrink-0">
        <button
          type="button"
          onClick={() => navigate(returnTo)}
          aria-label="Back"
          data-testid="retail-chat-back"
          className="text-emerald-200 hover:text-emerald-50"
        >
          <ArrowLeft className="w-5 h-5"/>
        </button>
        {tenant?.logo_url ? (
          <img src={resolveTenantLogoUrl(tenant.logo_url)} alt={`${tenantName} logo`} className="w-10 h-10 rounded-md object-contain bg-black"/>
        ) : (
          <div className="w-10 h-10 rounded-md bg-emerald-900 grid place-items-center"><Sparkles className="w-4 h-4 text-emerald-200"/></div>
        )}
        <div className="min-w-0 flex-1">
          <div className="font-serif text-lg leading-tight truncate" data-testid="retail-chat-tenant-name">{tenantName}</div>
          <div className="text-[10px] uppercase tracking-widest text-emerald-200/70 truncate inline-flex items-center gap-2 flex-wrap" data-testid="retail-chat-assistant-meta">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            {assistantName ? (
              <>
                <span className="text-emerald-100" data-testid="retail-chat-assistant-name">{assistantName}</span>
                {assistantRole && (
                  <span className="text-emerald-200/60 normal-case tracking-normal" data-testid="retail-chat-assistant-role">· {assistantRole}</span>
                )}
                <span className="text-emerald-200/40">· Live</span>
              </>
            ) : (
              "AI Assistant · Live"
            )}
          </div>
        </div>
        {/* Mobile sidebar toggle */}
        <button
          type="button"
          onClick={() => setSidebarOpen((v) => !v)}
          aria-label="Open quick prompts and contact"
          data-testid="retail-chat-sidebar-toggle"
          className="lg:hidden text-emerald-200 hover:text-emerald-50"
        >
          <MenuIcon className="w-5 h-5"/>
        </button>
        <button
          type="button"
          onClick={() => navigate(returnTo)}
          data-testid="retail-chat-start-sales-order"
          className="hidden sm:inline-flex items-center gap-2 px-4 py-2 rounded-full bg-orange-500 text-white text-sm font-semibold hover:bg-orange-600"
        >
          <ShoppingBag className="w-4 h-4"/> Start Sales Order
        </button>
      </header>

      {/* Body — desktop = sidebar + transcript ; mobile = transcript + drawer */}
      <div className="flex-1 flex min-h-0">
        {/* Persistent left sidebar (desktop) */}
        <aside
          className="hidden lg:flex flex-col w-72 shrink-0 border-r border-emerald-900/40 bg-[#0a1a0e]/70"
          data-testid="retail-chat-sidebar-desktop"
        >
          {SidebarContent}
        </aside>

        {/* Mobile drawer */}
        {sidebarOpen ? (
          <div className="lg:hidden fixed inset-0 z-40" role="dialog" aria-modal="true" data-testid="retail-chat-sidebar-mobile">
            <div className="absolute inset-0 bg-black/60" onClick={() => setSidebarOpen(false)} />
            <div className="absolute left-0 top-0 bottom-0 w-72 max-w-[85vw] bg-[#0a1a0e] border-r border-emerald-900/40 overflow-y-auto">
              <div className="flex items-center justify-between px-3 py-3 border-b border-emerald-900/40">
                <div className="font-serif text-emerald-100">Menu</div>
                <button
                  onClick={() => setSidebarOpen(false)}
                  aria-label="Close"
                  data-testid="retail-chat-sidebar-close"
                  className="text-emerald-200 hover:text-emerald-50"
                >
                  <X className="w-5 h-5"/>
                </button>
              </div>
              {SidebarContent}
            </div>
          </div>
        ) : null}

        {/* Transcript pane */}
        <main ref={scrollRef} className="flex-1 overflow-y-auto px-4 sm:px-8 py-6">
          <div className="max-w-3xl mx-auto">
            {transcript.length <= 1 && (
              <section className="mb-6 rounded-2xl border border-emerald-300/15 bg-[#102718]/70 backdrop-blur p-5 sm:p-6 text-center" data-testid="retail-chat-hero">
                <div className="mx-auto w-14 h-14 rounded-full bg-emerald-900/70 grid place-items-center border border-emerald-200/15">
                  <Sparkles className="w-6 h-6 text-emerald-200" />
                </div>
                <h1 className="mt-3 font-serif text-3xl sm:text-4xl text-emerald-50">
                  Ask {tenantName}&rsquo;s assistant anything.
                </h1>
                <p className="mt-2 text-sm sm:text-base text-emerald-200/70 max-w-lg mx-auto">
                  I&rsquo;m trained on this menu &mdash; flavors, ingredients, allergens, pairings, daily favorites. Tap a quick prompt on the left, or type below.
                </p>
              </section>
            )}

            <div className="space-y-3" data-testid="retail-chat-transcript">
              {transcript.map((m, i) => (
                <Bubble
                  key={i}
                  role={m.role}
                  text={m.text}
                  cards={m.cards}
                  pendingAction={m.pendingAction}
                  tenantName={tenantName}
                  slug={slug}
                  sessionId={sessionId}
                  catalogId={catalog?.id}
                />
              ))}
              {sending && (
                <div className="flex items-center gap-2 text-emerald-200/70 text-sm">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-bounce" style={{ animationDelay: "120ms" }} />
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-bounce" style={{ animationDelay: "240ms" }} />
                  <span className="text-xs">{tenantName} is thinking&hellip;</span>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>

      {/* Composer */}
      <footer className="bg-[#0a1a0e] border-t border-emerald-900/40 px-3 sm:px-6 py-3 shrink-0" data-testid="retail-chat-composer">
        <form onSubmit={(e) => { e.preventDefault(); send(); }} className="max-w-3xl mx-auto flex items-end gap-2">
          <button
            type="button"
            disabled
            title="Voice input is currently disabled. Coming soon."
            data-testid="retail-chat-voice-btn"
            className="h-11 w-11 grid place-items-center rounded-full bg-emerald-900/40 text-emerald-200/60 border border-emerald-200/15 cursor-not-allowed"
            aria-label="Voice input — coming soon"
          >
            <MicOff className="w-4 h-4" />
          </button>
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
            }}
            rows={1}
            placeholder={`Message the ${tenantName} assistant…`}
            data-testid="retail-chat-input"
            className="flex-1 resize-none rounded-2xl border border-emerald-900/40 bg-[#102718] px-4 py-3 text-sm text-emerald-50 placeholder:text-emerald-200/40 focus:outline-none focus:ring-1 focus:ring-orange-400 max-h-32"
          />
          <button
            type="submit"
            disabled={!input.trim() || sending}
            data-testid="retail-chat-send"
            className="h-11 w-11 grid place-items-center rounded-full bg-orange-500 text-white hover:bg-orange-600 disabled:bg-orange-500/40"
            aria-label="Send"
          >
            <Send className="w-4 h-4" />
          </button>
        </form>
        <button
          type="button"
          onClick={() => navigate(returnTo)}
          data-testid="retail-chat-start-sales-order-mobile"
          className="sm:hidden mt-3 w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-orange-500 text-white text-sm font-semibold hover:bg-orange-600"
        >
          <ShoppingBag className="w-4 h-4"/> Start Sales Order
        </button>
      </footer>
    </div>
  );
}

function Bubble({ role, text, cards, pendingAction, tenantName, slug, sessionId, catalogId }) {
  const isUser = role === "user";
  return (
    <div className={isUser ? "flex justify-end" : "flex items-start gap-2"}>
      {!isUser && (
        <div className="w-7 h-7 rounded-full bg-emerald-900/60 grid place-items-center text-emerald-200 shrink-0 mt-1" aria-hidden>
          <Sparkles className="w-3.5 h-3.5" />
        </div>
      )}
      <div className={
        "max-w-[88%] sm:max-w-[78%] flex flex-col gap-2 " +
        (isUser ? "items-end" : "items-start")
      }>
        <div className={
          "px-4 py-2.5 rounded-2xl text-sm leading-relaxed " +
          (isUser
            ? "bg-orange-500 text-white rounded-br-sm"
            : "bg-[#102718] text-emerald-50 border border-emerald-900/40 rounded-bl-sm")
        }>
          {!isUser && <div className="text-[10px] uppercase tracking-widest text-emerald-200/50 mb-0.5">{tenantName}</div>}
          <div className="whitespace-pre-wrap break-words">{text}</div>
        </div>
        {/* Iter62 — render product cards alongside the assistant turn. */}
        {!isUser && Array.isArray(cards) && cards.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 w-full" data-testid="retail-chat-cards">
            {cards.map((c) => (
              <ProductCardLite key={c.id || c.product_id || c.name} card={c} />
            ))}
          </div>
        ) : null}
        {/* Slice 4 (Feb 2026) — Assistant Add-to-Cart offer. Renders an
            explicit confirmation button. Never auto-adds. */}
        {!isUser && pendingAction && pendingAction.kind === "add_to_cart_offer" ? (
          <AddToOrderOffer
            offer={pendingAction}
            slug={slug}
            sessionId={sessionId}
            catalogId={catalogId}
          />
        ) : null}
      </div>
    </div>
  );
}

function AddToOrderOffer({ offer, slug, sessionId, catalogId }) {
  const [note, setNote] = useState((offer.suggested_modifiers || []).join(", "));
  const [state, setState] = useState("idle"); // idle | adding | added | failed
  const [errorMsg, setErrorMsg] = useState("");

  const onConfirm = async () => {
    if (state === "adding" || state === "added") return;
    setState("adding");
    setErrorMsg("");
    try {
      const body = {
        op: "add",
        session_id: sessionId,
        catalog_id: catalogId || offer.catalog_id || undefined,
        product_id: offer.product_id,
        quantity: offer.qty || 1,
        // Slice 4 provenance — backend writes an audit_log row when
        // these fields are present.
        source: "assistant_offer",
        offer_id: offer.offer_id,
        item_note: note.trim() || undefined,
        preferences_applied: offer.suggested_modifiers || [],
      };
      await axios.post(`${RETAIL_BASE}/${slug}/cart`, body);
      setState("added");
    } catch (_e) {
      setState("failed");
      setErrorMsg("Couldn't add — please tap Start Sales Order to add it from the menu.");
    }
  };

  return (
    <div
      className="w-full rounded-xl border border-orange-500/40 bg-orange-500/10 p-3 flex flex-col gap-2"
      data-testid="retail-chat-offer"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="text-xs uppercase tracking-wide text-orange-200/70">Suggested for you</div>
        <div className="text-sm font-semibold text-orange-100 truncate" data-testid="retail-chat-offer-product-name">
          {offer.product_name || "Selected item"}
        </div>
      </div>
      {(offer.suggested_modifiers || []).length > 0 && state !== "added" ? (
        <label className="text-xs text-emerald-100/70 flex flex-col gap-1" data-testid="retail-chat-offer-note-label">
          We&rsquo;ll add a note:
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            data-testid="retail-chat-offer-note-input"
            className="rounded-md border border-emerald-900/40 bg-[#0a1a0e] px-2 py-1 text-emerald-50 placeholder:text-emerald-200/40 focus:outline-none focus:ring-1 focus:ring-orange-400"
            placeholder="Optional notes for the kitchen"
          />
        </label>
      ) : null}
      {state === "added" ? (
        <div
          className="text-sm text-emerald-100 inline-flex items-center gap-2"
          data-testid="retail-chat-offer-added"
        >
          <span className="w-2 h-2 rounded-full bg-emerald-400" />
          Added {offer.qty || 1}× {offer.product_name || "item"} to your order
          {note.trim() ? ` — noted: ${note.trim()}` : ""}.
        </div>
      ) : (
        <button
          type="button"
          onClick={onConfirm}
          disabled={state === "adding"}
          data-testid="retail-chat-offer-confirm"
          className="self-start inline-flex items-center gap-2 px-4 py-2 rounded-full bg-orange-500 text-white text-sm font-semibold hover:bg-orange-600 disabled:bg-orange-500/40"
        >
          <ShoppingBag className="w-4 h-4" />
          {state === "adding" ? "Adding…" : (offer.confirm_label || "Add to your order")}
        </button>
      )}
      {state === "failed" && errorMsg ? (
        <div className="text-xs text-red-300" data-testid="retail-chat-offer-error">{errorMsg}</div>
      ) : null}
    </div>
  );
}

function ProductCardLite({ card }) {
  const name = card.name || card.product_name || "";
  const price = card.price_display || card.price || "";
  const img = card.image_url ? cleanImageUrl(card.image_url) : "";
  const desc = card.description || "";
  return (
    <div className="rounded-xl border border-emerald-900/40 bg-[#102718]/80 p-3 flex gap-3" data-testid="retail-chat-card">
      {img ? (
        <img src={img} alt={name} className="w-16 h-16 rounded-md object-cover shrink-0" />
      ) : (
        <div className="w-16 h-16 rounded-md bg-emerald-900/40 grid place-items-center shrink-0">
          <Sparkles className="w-4 h-4 text-emerald-200/50" />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-emerald-50 truncate" data-testid="retail-chat-card-name">{name}</div>
        {price ? <div className="text-xs text-orange-300 mt-0.5" data-testid="retail-chat-card-price">{price}</div> : null}
        {desc ? <div className="text-xs text-emerald-100/70 mt-1 line-clamp-2">{desc}</div> : null}
      </div>
    </div>
  );
}
